import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import AssessmentIcon from '@mui/icons-material/Assessment';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import GavelIcon from '@mui/icons-material/Gavel';
import ArticleIcon from '@mui/icons-material/Article';
import SchoolIcon from '@mui/icons-material/School';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import GetAppIcon from '@mui/icons-material/GetApp';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import GroupsIcon from '@mui/icons-material/Groups';
import nistService from '../services/nistService';
import { researchPaperCopyText } from './ResearchPaper';

// Copyable text content for each section
const cardCopyTexts = {
  marketValidation: `# NSCAT Market Validation Report (Revised)
## Network Security Configuration Audit Tool
### Deep-Dive Market Analysis with Web-Validated Data | January 2026

## Executive Summary - Revised Valuation

| Stage | Original Doc | First Review | Final Validated |
|-------|--------------|--------------|-----------------|
| Current MVP (Pre-Revenue) | $75K-$125K | $25K-$45K | $50,000 - $85,000 |
| Post-Traction ($50K-$100K ARR) | $150K-$350K | $125K-$250K | $200,000 - $450,000 |
| Exit Ceiling ($300K+ ARR) | N/A | $400K-$800K | $1M - $4M |

### Why the Upward Revision?
Initial analysis undervalued two enterprise-critical features:
1. SIEM Integration with real-time alerting (+$15K-$25K)
2. RAG-powered AI Chat with NIST SP 800-53 knowledge base (+$10K-$15K)

### Product Assessment
NSCAT is an AI-powered enterprise firewall security auditing platform that automates compliance assessments across multi-vendor firewall environments.

Strengths:
- Functional Multi-Vendor Support: 6 firewall types (Checkpoint, Cisco FTD, Fortinet, Huawei USG, Juniper vSRX, PaloAlto) | 535+ audits completed
- Comprehensive AI Integration: 18 Local LLMs (Ollama) + 3 Paid providers (OpenAI, Google AI, Groq, OpenRouter)
- NIST SP 800-53 Rev 5 Standards Coverage: 31 sections across 4 categories | RAG-based compliance mapping
- Working Features: PDF reports, SIEM integration, audit history, configuration storage, professional UI/UX

### Market Potential
Target Market Size: $1.5B - $2B (Firewall Management & Compliance)
- Mid-Market: $350M (500-5K employees, $8K-$35K/year)
- Enterprise: $800M (5K+ employees, $40K-$300K/year)
- MSSPs: $350M (Service revenue multiplier: 2-4x)

### Competitive Advantage
- AI-First Architecture: Only solution with RAG-powered compliance assistant, 21 model options
- Cost-Effective AI: 18 local LLMs = no ongoing AI API costs
- Price Point: Target $8K-$25K vs competitors $40K-$200K

### Investment Recommendation
Current Value: $50K-$85K | Post-Revenue: $200K-$450K | Exit Potential: $1M-$4M`,

  nscatPitch: `# NSCAT - AI-Powered Network Configuration Audit Platform
## Secure Mango LLC

### Key Metrics
- 6 Firewall Vendors: Cisco, PaloAlto, Huawei, Juniper, Checkpoint, Fortinet
- 535+ Audits Completed
- 21 AI Models (18 Local + Paid + Open-Source LLMs)
- 8 Integrated Modules: Dashboard, Audit, Reports, SIEM, SOAR, AI Chat, LLMs, RBAC

### The Problem
Firewall misconfigurations are responsible for 99% of firewall breaches (Gartner).
- Rules are complex, vendor-specific, and constantly changing
- Manual audits take weeks and cost $150-$300/hr per consultant
- Security teams are overloaded - auditing is deprioritized
- No cross-vendor, AI-native audit platform exists today
- Existing tools (Tufin, FireMon, AlgoSec) cost $40K-$200K/year and lack AI reasoning

### Our Solution
NSCAT automates the entire firewall audit lifecycle:
- AI-powered firewall configuration audit engine using Local, Paid & Open-Source LLMs
- Multi-vendor configuration ingestion from a live Network Lab
- Automated policy analysis correlated with NIST SP 800-53 Rev 5 Guidelines
- Risk scoring, PDF audit reports, and executive summaries
- Real-time SIEM alerting with severity-based triage
- SOAR-driven automated email dispatch to Network Teams with report links
- RAG-powered AI Chatbot for interactive audit explanation

### How It Works
1. Fetch Configuration → Pull firewall configs from multi-vendor devices
2. Normalize & Store → Parse vendor-specific formats into unified schema
3. AI Analysis → LLM-powered policy analysis
4. NIST Correlation → Map findings to NIST SP 800-53 Rev 5 Guidelines
5. Generate Report → PDF audit report with risk scores
6. SIEM Alert → Forward severity-based alerts to SOC via Syslog
7. SOAR Dispatch → Automated email via SOAR with report download link
8. AI Chatbot → Interactive audit explanation via RAG-powered chatbot

### Competition
| Feature | Incumbents | NSCAT |
|---------|-----------|-------|
| AI Audit Engine | None/Limited | 21 LLMs (Local + Paid + Open) |
| RAG AI Chatbot | None | NIST SP 800-53 RAG (1,014 controls) |
| SIEM + SOAR | Partial | Full end-to-end pipeline |
| Price | $40K-$200K/yr | Target: $8K-$25K/yr |

### Business Model
- SaaS subscription: Annual license per organization
- SMB: $5K-$8K/yr | Mid-Market: $8K-$15K/yr | Enterprise: $15K-$25K/yr | MSSP: $10K-$20K/yr

### The Ask
Seeking accelerator partnership to scale validated MVP into market-ready product.
- Enterprise & MSSP introductions for first 5-10 pilot customers
- Go-to-market mentorship for enterprise security sales
- Security ecosystem partnerships (SIEM/SOAR vendor integrations)
- Product-market fit validation through structured customer discovery`,

  provisionalPatent: `CONFIDENTIAL — PROVISIONAL PATENT DRAFT
PROVISIONAL PATENT APPLICATION — NSCAT
AI-Driven Multi-Vendor Network Configuration Audit and Compliance Orchestration System
Filed: February 2026 | Applicant: Secure Mango LLC

1. Field of Invention
This invention relates to automated network security configuration auditing systems, artificial intelligence assisted compliance analysis, and multi-vendor firewall configuration processing and orchestration platforms.

2. Background of the Invention
Firewall and network security configuration audits are typically manual, vendor-specific, or limited to static rule validation. Existing systems lack cross-vendor normalization, AI-assisted semantic analysis, automated standards correlation, and SOC/SIEM/SOAR orchestration.

3. Problem Statement
No existing solution combines: multi-vendor firewall ingestion, vendor-agnostic normalization, AI-driven audit analysis, multi-strategy score extraction, NIST SP 800-53 Rev 5 correlation, automated SOC orchestration with SIEM/SOAR, AI Chatbot, and RBAC-controlled user management in a single system.

4. Summary of the Invention
The invention provides a system for ingesting configurations from 6 firewall vendors, normalizing into a canonical data model, performing hybrid AI and rule-based audits, correlating with NIST SP 800-53 Rev 5, generating PDF reports, and dispatching alerts to SOC/SIEM/SOAR platforms. The MVP (NSCAT) has been validated in a multi-vendor network lab.

5. System Architecture
- Configuration Ingestion Engine (SSH + HTTPS API for 6 vendors)
- Vendor Normalization Layer (Canonical Data Model)
- Configuration Section Parser and Chunking Engine
- AI Audit Engine (6 LLM providers, 21 models)
- Vendor-Specific Prompt Engineering Framework
- Multi-Strategy Compliance Score Extraction (4-level fallback)
- NIST SP 800-53 Rev 5 Compliance Mapping (RAG Semantic Search)
- Alert Classification and SOC Orchestration (6-tier severity, SIEM/SOAR/Email)

13. Claims
Claim 1 (System): Computer-implemented system for automated multi-vendor network security configuration auditing with configuration ingestion, vendor normalization, AI audit engine, prompt engineering, score extraction, compliance mapping, alert orchestration, and RBAC.
Claim 2 (Method): Computer-implemented method for auditing network security configurations end-to-end.
Claim 3 (Score Extraction): Method for extracting compliance scores from heterogeneous AI outputs via 4-level fallback.
Claim 4 (Orchestration): Method for automated SOC orchestration with parallel SIEM/SOAR/report/email dispatch.

CONFIDENTIAL — Secure Mango LLC, 2026. All rights reserved.`,

  researchPaper: researchPaperCopyText,

  ragGroundTruth: `# RAG NIST Mapping Ground Truth — Latest 100 Findings

## Methodology
- Sample: 100 most-recent findings across 14 most-recent direct-audit reports (2026-05-03 cohort)
- Annotator: First-author baseline
- Scoring: Top-1 accuracy with two schemes
  - Strict: c only
  - Lenient: c + 0.5 * p
- Rubric: c=correct, p=partial (right family OR adjacent control, sub-control off), w=wrong (different security domain)

## Headline Results
- N = 100
- Correct (c) = 27
- Partial (p) = 24
- Wrong (w) = 49
- Top-1 strict accuracy = 27.0%
- Top-1 lenient accuracy = 39.0%

## Per-NIST-Family Highlights
- SC (51 findings): 45.1% strict / 60.8% lenient — strongest family
- SI (20 findings): 10.0% strict / 12.5% lenient — SI-19(02) systematic miscategorisation
- CM (18 findings): 5.6% strict / 19.4% lenient — CM-02(07) over-applied

## Per-Vendor Highlights
- Checkpoint (24): 41.7% strict / 54.2% lenient
- Palo Alto (18): 27.8% strict / 41.7% lenient
- Juniper SRX (20): 25.0% strict / 35.0% lenient
- Cisco FTD (27): 11.1% strict / 24.1% lenient — pulls average down

## Reference
Supports Paper 1 §VII-D RAG precision claim. The 99.96% hit rate measures coverage; this dataset measures correctness.

CONFIDENTIAL — Secure Mango LLC, 2026. All rights reserved.`,

  stratifiedGroundTruth: `# Stratified Ground Truth — Paper 2

## Methodology
- Sample: 96 findings stratified across 6 vendor platforms × 4 severity levels (4 per cell)
- Source: full 303-audit production corpus (no exclusion of the recency cohort used in Paper 1)
- Sampling seed: 42 (reproducible)
- Annotator: First-author baseline annotation (pending)
- Scoring: Top-1 accuracy with two schemes (strict = c only; lenient = c + 0.5p)

## Stratification
- 16 findings per vendor: Cisco FTD, Palo Alto PAN-OS, Juniper SRX, Fortinet FortiGate, Huawei USG, Check Point Security Gateway
- 4 findings per severity per vendor: Critical / High / Medium / Low

## Purpose
Paper 2 §III-D introduces this dataset; §V-A uses it for the three-way NIST mapping strategy comparison (keyword categorical vs. RAG hybrid vs. LLM-native citation). The stratified design avoids the vendor-imbalance and severity-skew of the recency cohort, allowing per-stratum precision claims that Paper 1's "latest-100" set cannot support.

## Status
Annotations populated via the inline editor at /mvp-overview/stratified-ground-truth.

CONFIDENTIAL — Secure Mango LLC, 2026. All rights reserved.`,
};

function MVPOverview() {
  const navigate = useNavigate();
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [downloadingIndex, setDownloadingIndex] = useState(null);

  const handleCopy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnackbar({ open: true, message: `${label} copied to clipboard` });
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setSnackbar({ open: true, message: `${label} copied to clipboard` });
      } catch (fallbackErr) {
        console.error('Copy failed:', fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleDownloadMarketValidation = async () => {
    setDownloadingIndex(0);
    try {
      const blob = await nistService.downloadMarketValidationReport();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `NSCAT_Market_Validation_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setSnackbar({ open: true, message: 'Market Validation Report downloaded' });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setSnackbar({ open: true, message: 'Download failed - please try from the detail page' });
    } finally {
      setDownloadingIndex(null);
    }
  };

  const handleDownloadPitch = () => {
    setDownloadingIndex(1);
    try {
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>NSCAT - AI-Powered Network Configuration Audit Platform</title>
            <style>
              @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap');
              * { margin: 0; padding: 0; box-sizing: border-box; }
              body { font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif; color: #1e293b; background: white; }
              .page { page-break-after: always; padding: 50px 56px; min-height: 100vh; position: relative; }
              .page:last-child { page-break-after: auto; }
              h1 { font-size: 32px; font-weight: 700; margin-bottom: 6px; color: #1e293b; }
              h2 { font-size: 20px; font-weight: 600; margin-bottom: 14px; color: #5c6bc0; border-bottom: 2px solid #5c6bc0; padding-bottom: 6px; }
              h3 { font-size: 15px; font-weight: 500; margin-bottom: 6px; color: #475569; }
              p { font-size: 13px; line-height: 1.7; margin-bottom: 6px; color: #334155; }
              ul { list-style: none; padding: 0; }
              ul li { font-size: 13px; line-height: 1.9; padding-left: 18px; position: relative; color: #334155; }
              ul li:before { content: "\\2022"; color: #5c6bc0; font-weight: bold; position: absolute; left: 2px; }
              .section { margin-bottom: 24px; }
              .two-col { display: flex; gap: 36px; }
              .two-col > div { flex: 1; }
              .cover { display: flex; flex-direction: column; justify-content: center; align-items: center; text-align: center; }
              .cover h1 { font-size: 40px; color: #5c6bc0; margin-bottom: 4px; }
              .cover .tagline { font-size: 17px; color: #475569; margin-bottom: 28px; font-weight: 400; }
              .cover .points { margin-top: 8px; }
              .cover .points li { font-size: 15px; line-height: 2; }
              .cover .company { margin-top: 36px; font-size: 14px; color: #94a3b8; font-weight: 500; }
              .stat-row { display: flex; gap: 16px; margin: 16px 0; }
              .stat-box { flex: 1; background: #f1f5f9; border-radius: 8px; padding: 12px; text-align: center; }
              .stat-box .num { font-size: 22px; font-weight: 700; color: #5c6bc0; }
              .stat-box .label { font-size: 11px; color: #64748b; margin-top: 2px; }
              table { width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 12px; }
              th, td { padding: 8px 10px; text-align: left; border-bottom: 1px solid #e2e8f0; }
              th { background: #f1f5f9; font-weight: 600; color: #475569; }
              .highlight { background: #eff6ff; border-left: 3px solid #5c6bc0; padding: 10px 14px; border-radius: 0 6px 6px 0; margin: 12px 0; font-size: 13px; }
              .footer { position: absolute; bottom: 24px; left: 56px; right: 56px; text-align: center; font-size: 10px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 8px; }
              @media print { .page { padding: 44px 50px; } .footer { bottom: 20px; left: 50px; right: 50px; } }
            </style>
          </head>
          <body>
            <div class="page cover">
              <h1>NSCAT</h1>
              <p class="tagline">AI-Powered Network Configuration Audit Platform</p>
              <ul class="points">
                <li>Automated AI firewall configuration auditing across 6 vendors</li>
                <li>End-to-end SOC + SIEM + SOAR integration</li>
                <li>RAG-powered AI Chatbot with NIST SP 800-53 knowledge base</li>
                <li>Built for Enterprises, SOC Teams & MSSPs</li>
              </ul>
              <p class="company">Secure Mango LLC</p>
              <div class="footer">NSCAT - Confidential</div>
            </div>
            <div class="page">
              <div class="section">
                <h2>The Problem</h2>
                <p>Firewall misconfigurations are responsible for 99% of firewall breaches (Gartner).</p>
                <ul>
                  <li>Firewall misconfigurations are the #1 cause of network breaches</li>
                  <li>Rules are complex, vendor-specific, and constantly changing</li>
                  <li>Manual audits take weeks and cost $150-$300/hr per consultant</li>
                  <li>Existing tools (Tufin, FireMon, AlgoSec) cost $40K-$200K/year and lack AI reasoning</li>
                </ul>
              </div>
              <div class="section">
                <h2>Our Solution</h2>
                <ul>
                  <li>AI-powered firewall configuration audit engine using Local, Paid & Open-Source LLMs</li>
                  <li>Multi-vendor configuration ingestion from a live Network Lab</li>
                  <li>Automated policy analysis correlated with NIST SP 800-53 Rev 5 Guidelines</li>
                  <li>Real-time SIEM alerting with severity-based triage</li>
                  <li>SOAR-driven automated email dispatch with report links</li>
                  <li>RAG-powered AI Chatbot for interactive audit explanation</li>
                </ul>
              </div>
              <div class="section">
                <h2>Product - MVP Built & Operational</h2>
                <div class="stat-row">
                  <div class="stat-box"><div class="num">6</div><div class="label">Firewall Vendors</div></div>
                  <div class="stat-box"><div class="num">535+</div><div class="label">Audits Completed</div></div>
                  <div class="stat-box"><div class="num">21</div><div class="label">AI Models</div></div>
                  <div class="stat-box"><div class="num">8</div><div class="label">Integrated Modules</div></div>
                </div>
              </div>
              <div class="section">
                <h2>Competition & Advantage</h2>
                <table>
                  <tr><th>Feature</th><th>Incumbents</th><th>NSCAT</th></tr>
                  <tr><td>AI Audit Engine</td><td>None / Limited</td><td>21 LLMs (Local + Paid + Open)</td></tr>
                  <tr><td>RAG Chatbot</td><td>None</td><td>NIST SP 800-53 RAG (1,014 controls)</td></tr>
                  <tr><td>SIEM + SOAR</td><td>Partial</td><td>Full end-to-end pipeline</td></tr>
                  <tr><td>Price</td><td>$40K-$200K/yr</td><td>Target: $8K-$25K/yr</td></tr>
                </table>
                <div class="highlight"><strong>Key Differentiator:</strong> Only platform combining multi-vendor AI-native auditing, RAG-powered compliance chatbot, and full SOC/SIEM/SOAR pipeline at 5-10x lower price point.</div>
              </div>
              <div class="footer">NSCAT - Confidential | Secure Mango LLC</div>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.onload = () => { printWindow.print(); };
      setSnackbar({ open: true, message: 'Pitch deck opened for download' });
    } catch (error) {
      console.error('Error generating pitch PDF:', error);
    } finally {
      setDownloadingIndex(null);
    }
  };

  const handleDownloadPatent = () => {
    setDownloadingIndex(2);
    try {
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>NSCAT Provisional Patent Draft</title>
            <style>
              @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap');
              * { margin: 0; padding: 0; box-sizing: border-box; }
              body { font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif; color: #1e293b; background: white; padding: 50px 56px; }
              h1 { font-size: 28px; font-weight: 700; margin-bottom: 4px; color: #1e293b; text-align: center; }
              h2 { font-size: 18px; font-weight: 600; margin: 24px 0 10px; color: #7b1fa2; border-bottom: 2px solid #7b1fa2; padding-bottom: 4px; }
              h3 { font-size: 14px; font-weight: 600; margin: 12px 0 6px; color: #475569; }
              p { font-size: 12px; line-height: 1.8; margin-bottom: 8px; color: #334155; text-align: justify; }
              .subtitle { font-size: 14px; color: #475569; text-align: center; margin-bottom: 4px; }
              .meta { font-size: 12px; color: #94a3b8; text-align: center; margin-bottom: 24px; }
              .confidential { background: #fef2f2; border: 1px solid #ef4444; padding: 10px; text-align: center; font-size: 11px; color: #dc2626; font-weight: 600; margin: 20px 0; border-radius: 4px; }
              ul { padding-left: 20px; margin-bottom: 8px; }
              ul li { font-size: 12px; line-height: 1.8; color: #334155; }
              .claim { background: #eff6ff; border-left: 3px solid #7b1fa2; padding: 10px 14px; margin: 10px 0; font-size: 12px; line-height: 1.7; border-radius: 0 4px 4px 0; }
              @media print { body { padding: 40px 44px; } }
            </style>
          </head>
          <body>
            <div class="confidential">CONFIDENTIAL — PROVISIONAL PATENT DRAFT</div>
            <h1>NSCAT</h1>
            <p class="subtitle">AI-Driven Multi-Vendor Network Configuration Audit and Compliance Orchestration System</p>
            <p class="meta">Filed: February 2026 | Applicant: Secure Mango LLC</p>

            <h2>1. Field of Invention</h2>
            <p>This invention relates to automated network security configuration auditing systems, artificial intelligence assisted compliance analysis, and multi-vendor firewall configuration processing and orchestration platforms.</p>

            <h2>2. Background</h2>
            <p>Firewall and network security configuration audits are typically manual, vendor-specific, or limited to static rule validation. Existing systems lack cross-vendor normalization, AI-assisted semantic analysis, automated standards correlation, and SOC/SIEM/SOAR orchestration.</p>

            <h2>3. Problem Statement</h2>
            <p>No existing solution combines multi-vendor firewall ingestion, vendor-agnostic normalization, AI-driven audit analysis, multi-strategy score extraction, NIST SP 800-53 Rev 5 correlation, automated SOC orchestration, AI Chatbot, and RBAC user management in a single integrated system.</p>

            <h2>4. Summary of the Invention</h2>
            <p>The invention provides a system for ingesting configurations from 6 firewall vendors, normalizing into a canonical data model, performing hybrid AI and rule-based audits, correlating with NIST SP 800-53 Rev 5, generating PDF reports, and dispatching alerts to SOC/SIEM/SOAR platforms. The MVP (NSCAT) has been validated in a multi-vendor network lab environment comprising Cisco ASAv/FTD, Palo Alto PAN-OS VM, Juniper vSRX, Huawei USG, FortiGate VM, and Check Point Security Gateway.</p>

            <h2>5. System Architecture</h2>
            <p><strong>5.1</strong> Configuration Ingestion Engine (SSH + HTTPS API for 6 vendors)<br/>
            <strong>5.2</strong> Vendor Normalization Layer (Canonical Data Model)<br/>
            <strong>5.3</strong> Configuration Section Parser and Chunking Engine<br/>
            <strong>5.4</strong> AI Audit Engine (6 LLM providers, 21 models)<br/>
            <strong>5.5</strong> Vendor-Specific Prompt Engineering Framework<br/>
            <strong>5.6</strong> Multi-Strategy Compliance Score Extraction (4-level fallback)<br/>
            <strong>5.7</strong> NIST SP 800-53 Rev 5 Compliance Mapping (RAG Semantic Search)<br/>
            <strong>5.8</strong> Alert Classification and SOC Orchestration (6-tier severity, SIEM/SOAR/Email)</p>

            <h2>13. Claims</h2>
            <div class="claim"><strong>Claim 1 (System):</strong> A computer-implemented system for automated multi-vendor network security configuration auditing comprising configuration ingestion, vendor normalization, AI audit engine, prompt engineering, score extraction, compliance mapping, alert orchestration, and RBAC.</div>
            <div class="claim"><strong>Claim 2 (Method):</strong> A computer-implemented method for auditing network security configurations end-to-end from ingestion through alert dispatch.</div>
            <div class="claim"><strong>Claim 3 (Score Extraction):</strong> A method for extracting compliance scores from heterogeneous AI model outputs via 4-level fallback mechanism.</div>
            <div class="claim"><strong>Claim 4 (Orchestration):</strong> A method for automated security operations orchestration with parallel SIEM/SOAR/report/email dispatch.</div>

            <div class="confidential">CONFIDENTIAL — Secure Mango LLC, 2026. All rights reserved.</div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.onload = () => { printWindow.print(); };
      setSnackbar({ open: true, message: 'Patent draft opened for download' });
    } catch (error) {
      console.error('Error generating patent PDF:', error);
    } finally {
      setDownloadingIndex(null);
    }
  };

  const handleDownloadResearchPaper = () => {
    setDownloadingIndex(3);
    try {
      const printWindow = window.open('', '_blank');
      const escapedAbstract = researchPaperCopyText.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      printWindow.document.write(`
        <html>
          <head>
            <title>NSCAT Research Paper — Abstract</title>
            <style>
              body { font-family: 'Georgia', serif; color: #1a1a1a; padding: 56px; line-height: 1.9; font-size: 12px; }
              .header { text-align: center; margin-bottom: 28px; padding-bottom: 16px; border-bottom: 2px solid #1565c0; }
              .header h1 { font-size: 20px; font-weight: 700; margin-bottom: 10px; line-height: 1.4; }
              .header .authors { font-size: 13px; color: #333; font-style: italic; }
              .header .affiliation { font-size: 11px; color: #666; }
              .content { text-align: justify; white-space: pre-line; }
              @media print { body { padding: 44px 50px; } }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>NSCAT: A Multi-Vendor Firewall Compliance Framework with Triple-Strategy NIST SP 800-53 Mapping, Explainable AI, and SOC Orchestration</h1>
              <p class="authors">Naeem Amjad, Attiq Ahmad, Imran Rashid</p>
              <p class="affiliation">Department of Information Security, NUST, Islamabad, Pakistan</p>
            </div>
            <div class="content">${escapedAbstract}</div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.onload = () => { printWindow.print(); };
      setSnackbar({ open: true, message: 'Research paper opened for download' });
    } catch (error) {
      console.error('Error generating research paper PDF:', error);
    } finally {
      setDownloadingIndex(null);
    }
  };

  const downloadHandlers = [handleDownloadMarketValidation, handleDownloadPitch, handleDownloadPatent, handleDownloadResearchPaper];
  const copyKeys = ['marketValidation', 'nscatPitch', 'provisionalPatent', 'researchPaper'];

  // Documentation cards (base definition)
  const allCards = [
    {
      id: 'marketValidation',
      title: 'NSCAT Market Validation Report',
      subtitle: 'Deep-Dive Market Analysis with Web-Validated Data | January 2026',
      description: 'Comprehensive market validation including revised valuation ($50K-$85K), product assessment, competitive positioning, funding options, and Pakistan market analysis.',
      icon: <AssessmentIcon sx={{ fontSize: 48 }} />,
      color: 'primary.main',
      bgGradient: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
      chips: ['Valuation', 'Market Analysis', 'Competitive', 'Funding'],
      route: '/mvp-overview/market-validation',
    },
    {
      id: 'nscatPitch',
      title: 'NSCAT Pitch',
      subtitle: 'AI-Powered Network Configuration Audit Platform',
      description: 'Pitch deck covering problem, solution, MVP product, market & customers, competitive advantage, business model, and Techstars ask.',
      icon: <RocketLaunchIcon sx={{ fontSize: 48 }} />,
      color: 'secondary.main',
      bgGradient: 'linear-gradient(135deg, #1a2332 0%, #0d1117 100%)',
      chips: ['Pitch Deck', 'Product', 'Market', 'Business Model'],
      route: '/mvp-overview/nscat-pitch',
    },
    {
      id: 'provisionalPatent',
      title: 'NSCAT Provisional Patent Draft',
      subtitle: 'AI-Driven Multi-Vendor Network Configuration Audit & Compliance Orchestration | February 2026',
      description: 'USPTO provisional patent draft covering system architecture, claims, technical methods, algorithms, multi-vendor normalization, prior art distinction, and compliance orchestration.',
      icon: <GavelIcon sx={{ fontSize: 48 }} />,
      color: '#ab47bc',
      bgGradient: 'linear-gradient(135deg, #2d1b3d 0%, #1a0e2e 100%)',
      chips: ['Patent', 'Claims', 'Architecture', 'Prior Art'],
      route: '/mvp-overview/provisional-patent',
    },
    {
      id: 'researchPaper',
      title: '1st Research Paper',
      subtitle: 'NSCAT: Multi-Vendor Firewall Compliance with NIST Mapping, xAI, and SOC | IEEE Format | 2025',
      description: 'Research paper with abstract covering framework design, canonical data model, enhanced RAG pipeline, context-aware prompting, hybrid audit engine, and SOC orchestration.',
      icon: <SchoolIcon sx={{ fontSize: 48 }} />,
      color: '#1565c0',
      bgGradient: 'linear-gradient(135deg, #0d2137 0%, #071318 100%)',
      chips: ['Abstract', 'LaTeX', 'IEEE', 'Framework Design'],
      route: '/mvp-overview/research-paper',
    },
    {
      id: 'researchPaper2',
      title: '2nd Research Paper',
      subtitle: 'Benchmarking 21 LLMs Across Six Firewall Vendors | IEEE Format | 2025',
      description: 'Empirical evaluation paper covering LLM provider benchmarks, score extraction pipeline analysis, NIST control mapping accuracy, vendor-specific audit results, and operational deployment metrics across 535+ audits.',
      icon: <SchoolIcon sx={{ fontSize: 48 }} />,
      color: '#00695c',
      bgGradient: 'linear-gradient(135deg, #0d2b2b 0%, #071a18 100%)',
      chips: ['Audit Results', 'LLM Benchmark', 'Metrics', 'Empirical'],
      route: '/mvp-overview/research-paper-2',
    },
    {
      id: 'ragGroundTruth',
      title: 'RAG NIST Mapping Ground Truth',
      subtitle: 'Top-1 Precision Evaluation on Latest 100 Findings | 2026-05-03 Cohort',
      description: 'Per-finding ground-truth dataset evaluating the RAG-based NIST SP 800-53 mapping pipeline. Strict 27% / lenient 39% top-1 accuracy across 100 most-recent findings, with per-NIST-family and per-vendor breakdowns; supports Paper 1 §VII-D precision claim.',
      icon: <SchoolIcon sx={{ fontSize: 48 }} />,
      color: '#b85e00',
      bgGradient: 'linear-gradient(135deg, #2d1a0a 0%, #1a0e05 100%)',
      chips: ['Ground Truth', 'Precision', 'RAG', '100 Findings'],
      route: '/mvp-overview/rag-ground-truth',
    },
    {
      id: 'stratifiedGroundTruth',
      title: 'Stratified Ground Truth (Paper 2)',
      subtitle: 'Stratified Sample — 6 Vendors × 4 Severities | seed=42',
      description: 'Independent 96-finding stratified ground-truth sample drawn from all 303 completed audits with no exclusion. 16 findings per vendor balanced across Critical / High / Medium / Low severities. Used by Paper 2 §III-D and §V-A for the three-way NIST mapping strategy comparison (keyword categorical vs. RAG hybrid vs. LLM-native citation).',
      icon: <FactCheckIcon sx={{ fontSize: 48 }} />,
      color: '#1565c0',
      bgGradient: 'linear-gradient(135deg, #0a1929 0%, #051221 100%)',
      chips: ['Stratified', 'Paper 2', 'Three-Way Mapping', '96 Findings'],
      route: '/mvp-overview/stratified-ground-truth',
    },
    {
      id: 'paper1AnnotationStats',
      title: 'Paper1 Annotation Stats',
      subtitle: 'Two-Annotator Agreement Study — COMNET-D-26-05631 Revision',
      description: 'Live inter-annotator-agreement study answering Computer Networks reviewers R1-8 (annotation process and how agreement was measured), R2-2 and R4-1 (two security experts independent of the author team). Two annotators label the same blinded 200-finding queue without seeing each other\'s verdicts. Reports Cohen\'s kappa unweighted and linear-weighted, raw percentage agreement, the 3x3 confusion matrix, per-cohort and per-vendor top-1 precision, and the adjudication queue.',
      icon: <GroupsIcon sx={{ fontSize: 48 }} />,
      color: '#00897b',
      bgGradient: 'linear-gradient(135deg, #04201d 0%, #021412 100%)',
      chips: ['Cohen\'s κ', 'Blinded', '2 Annotators', '200 Findings'],
      route: '/mvp-overview/paper1-annotation-stats',
    },
    {
      id: 'aiInferredClassification',
      title: 'Verified / AI-Inferred Validation',
      subtitle: 'Does the Configuration Support the Finding? — COMNET-D-26-05631 Revision',
      description: 'Answers Computer Networks reviewers R1-18 (how often AI-inferred findings are genuine hallucinations rather than valid implicit inferences) and R2 area 7 (validate the Verified/AI-inferred mechanism more directly). The tag records only whether a finding could be tied to a configuration line; it cannot distinguish an inference the model could not anchor from an outright fabrication. All 231 tagged findings from the post-deployment subset are judged by hand against the device configuration on one question -- does the configuration support this finding? -- so the answer can be crossed against the label the system applied. That cross-tabulation gives both of the mechanism\'s error directions: findings it vouched for that are unsupported, and findings it hedged on that are sound. Classification is performed at /classifier by a dedicated account.',
      icon: <GroupsIcon sx={{ fontSize: 48 }} />,
      color: '#6a1b9a',
      bgGradient: 'linear-gradient(135deg, #1a0a22 0%, #10060f 100%)',
      chips: ['231 Findings', 'R1-18', 'R2 Area 7', 'Provenance'],
      route: '/mvp-overview/ai-inferred-stats',
    },
  ];

  // Drag-and-drop reordering
  const [cardOrder, setCardOrder] = useState(() => {
    try {
      const saved = localStorage.getItem('nscat_knowledgeBase_cardOrder');
      if (saved) {
        const order = JSON.parse(saved);
        // Validate: must contain all current card IDs
        const allIds = allCards.map(c => c.id);
        if (order.length === allIds.length && allIds.every(id => order.includes(id))) return order;
      }
    } catch {}
    return allCards.map(c => c.id);
  });
  const [dragOverId, setDragOverId] = useState(null);
  const dragItemId = useRef(null);

  const documentationCards = cardOrder.map(id => allCards.find(c => c.id === id)).filter(Boolean);

  const handleDragStart = useCallback((e, cardId) => {
    dragItemId.current = cardId;
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback((e, cardId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (cardId !== dragOverId) setDragOverId(cardId);
  }, [dragOverId]);

  const handleDrop = useCallback((e, targetId) => {
    e.preventDefault();
    setDragOverId(null);
    const sourceId = dragItemId.current;
    if (!sourceId || sourceId === targetId) return;
    setCardOrder(prev => {
      const newOrder = [...prev];
      const fromIdx = newOrder.indexOf(sourceId);
      const toIdx = newOrder.indexOf(targetId);
      newOrder.splice(fromIdx, 1);
      newOrder.splice(toIdx, 0, sourceId);
      localStorage.setItem('nscat_knowledgeBase_cardOrder', JSON.stringify(newOrder));
      return newOrder;
    });
  }, []);

  const handleDragEnd = useCallback(() => {
    dragItemId.current = null;
    setDragOverId(null);
  }, []);

  const iconBtnSx = {
    color: 'rgba(255,255,255,0.7)',
    '&:hover': { color: 'white', backgroundColor: 'rgba(255,255,255,0.15)' },
    '&:disabled': { color: 'rgba(255,255,255,0.3)' },
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 4, backgroundColor: '#1e293b', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <BusinessIcon sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h4" fontWeight={600}>
              Knowledge Base
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              NSCAT Documentation & Reports Hub
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Documentation Cards Grid */}
      <Grid container spacing={3}>
        {documentationCards.map((card) => {
          const origIdx = allCards.findIndex(c => c.id === card.id);
          return (
          <Grid item xs={12} sm={6} md={4} key={card.id}>
            <Card
              draggable
              onDragStart={(e) => handleDragStart(e, card.id)}
              onDragOver={(e) => handleDragOver(e, card.id)}
              onDrop={(e) => handleDrop(e, card.id)}
              onDragEnd={handleDragEnd}
              onClick={() => navigate(card.route)}
              sx={{
                cursor: 'pointer',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderLeft: '4px solid',
                borderColor: card.color,
                transition: 'all 0.3s ease-in-out',
                opacity: dragOverId === card.id ? 0.6 : 1,
                transform: dragOverId === card.id ? 'scale(0.97)' : 'none',
                outline: dragOverId === card.id ? '2px dashed' : 'none',
                outlineColor: dragOverId === card.id ? 'primary.main' : 'transparent',
                '&:hover': {
                  transform: dragOverId ? undefined : 'translateY(-4px)',
                  boxShadow: dragOverId ? undefined : 8,
                },
              }}
            >
              {/* Card Icon Header */}
              <Box
                sx={{
                  background: card.bgGradient,
                  p: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <DragIndicatorIcon
                    sx={{ color: 'rgba(255,255,255,0.4)', cursor: 'grab', '&:active': { cursor: 'grabbing' }, fontSize: 20 }}
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                  <Box sx={{ color: 'white' }}>
                    {card.icon}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Tooltip title={`Copy ${card.title} content`}>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleCopy(cardCopyTexts[card.id], card.title); }}
                      sx={iconBtnSx}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={`Download ${card.title} PDF`}>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); if (downloadHandlers[origIdx]) downloadHandlers[origIdx](); }}
                      disabled={downloadingIndex === origIdx || !downloadHandlers[origIdx]}
                      sx={iconBtnSx}
                    >
                      {downloadingIndex === origIdx
                        ? <CircularProgress size={18} sx={{ color: 'rgba(255,255,255,0.7)' }} />
                        : <GetAppIcon fontSize="small" />
                      }
                    </IconButton>
                  </Tooltip>
                  <ChevronRightIcon sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 28, ml: 0.5 }} />
                </Box>
              </Box>

              <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {card.title}
                </Typography>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  {card.subtitle}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2, flex: 1 }}>
                  {card.description}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {card.chips.map((chip) => (
                    <Chip key={chip} label={chip} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
          );
        })}

        {/* Placeholder Card for Future Docs */}
        <Grid item xs={12} sm={6} md={4}>
          <Card
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              border: '2px dashed',
              borderColor: 'divider',
              backgroundColor: 'transparent',
              minHeight: 280,
              opacity: 0.5,
            }}
          >
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
              <ArticleIcon sx={{ fontSize: 48, mb: 2, opacity: 0.4 }} />
              <Typography variant="subtitle1" color="text.secondary" fontWeight={500}>
                More Documentation Coming Soon
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Future NSCAT docs will appear here
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Snackbar Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

export default MVPOverview;
